import { __awaiter, __asyncValues } from 'tslib';
import * as Long from 'long';
import '../../proto/index.js';
import '../../core/consts.js';
import { Logger } from '../../core/metrics/logger.js';
import '../../core/metrics/monitor.js';
import '../../core/service/RuntimeContext.js';
import '../../core/network/NetworkItem.js';
import 'json-long';
import 'moize';
import '../../core/model/Message.js';
import 'js-md5';
import '../../core/utils/lz4.js';
import '../../core/utils/serializer.js';
import '../../adapter/manager/AdapterManager.js';
import '../../adapter/manager/types.js';
import '../../core/utils/ticker.js';
import '../../adapter/abstract/IMNetApi.js';
import '../../core/options.js';
import '../../core/utils/uuid.js';
import '../../core/service/ConversationManager.js';
import '../../core/service/MessageManager.js';
import '@byted/uc-secure-sdk';
import { DbProxy, ConfigKeys } from '../../core/plugin/DbProxy.js';
import '../../core/metrics/trackerTypes.js';
import '../../core/model/CommandMessage.js';
import { openDB, deleteDB } from 'idb/with-async-ittr';
import { ConversationStore } from '../model/ConversationStore.js';
import { MessageStore } from '../model/MessageStore.js';
import { ParticipantStore } from '../model/ParticipantStore.js';
import { generateKeyFromPassPhrase, decrypt, encrypt } from '../util/crypto.js';

class IndexedDbDriver extends DbProxy {
    init(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.currentUserId = userId;
            this.db = yield openDB(`${IndexedDbDriver.DbNamePrefix}:${this.ctx.option.boe ? 'boe' : 'prod'}:${this.ctx.option.appId}:${userId}`, IndexedDbDriver.DbVersion, {
                upgrade(db, oldVersion, newVersion, tx) {
                    // version v1, no need to upgrade
                    db.createObjectStore('config');
                    db.createObjectStore('conversation', {
                        keyPath: 'id',
                        autoIncrement: false
                    });
                    db.createObjectStore('message', {
                        autoIncrement: false
                    })
                        .createIndex('__index_con_id', 'conversationId');
                    db.createObjectStore('participant', {
                        autoIncrement: false
                    })
                        .createIndex('__index_con_id', 'conversationId');
                    db.createObjectStore('cursor');
                }
            });
            // use db version to determine whether need to load from db
            let isUsingDbBefore = (yield this.db.get('config', ConfigKeys.DbVersion)) !== undefined;
            const isUsingEncrpytionBefore = (yield this.db.get('config', ConfigKeys.DbEncyption)) === 'true';
            if (this.ctx.option.encryption) {
                this.encryptionEnabled = true;
                Logger.ctxDebug(this.ctx, 'storage encryption enabled');
            }
            else {
                yield this.deleteConfig(ConfigKeys.DbEncyption);
                this.encryptionEnabled = false;
            }
            if (this.encryptionEnabled) {
                const generateKey = yield generateKeyFromPassPhrase(this.ctx, this.currentUserId);
                if (generateKey) {
                    this._key = generateKey;
                }
                else {
                    Logger.ctxError(this.ctx, 'load encryption key failed');
                    this.encryptionEnabled = false;
                }
            }
            // normal --> encryption
            if (isUsingDbBefore && !isUsingEncrpytionBefore && this.encryptionEnabled) {
                yield this.clear(true);
                Logger.ctxWarn(this.ctx, 'try to load unencrypted data but encryption enabled, clear local data');
                isUsingDbBefore = false;
            }
            // encryption not enabled but has encrypted data
            if (isUsingDbBefore && isUsingEncrpytionBefore && !this.encryptionEnabled) {
                // clear local cache
                yield this.clear(true);
                Logger.ctxWarn(this.ctx, 'try to load encrpyted data but not enabled, clear local data');
                isUsingDbBefore = false;
            }
            // write last open and new db version
            yield this.db.put('config', IndexedDbDriver.DbVersion.toString(), ConfigKeys.DbVersion);
            yield this.db.put('config', Date.now().toString(), ConfigKeys.DbLastOpen);
            yield this.db.put('config', this.encryptionEnabled.toString(), ConfigKeys.DbEncyption);
            return isUsingDbBefore;
        });
    }
    loadConversation() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const list = yield this.db.getAll('conversation');
            if (this.encryptionEnabled) {
                const dListPromise = list.map((item) => decrypt(this.ctx, this._key, item));
                const deConvRes = yield Promise.allSettled(dListPromise);
                const convList = [];
                for (const item of deConvRes) {
                    if (item.status === 'fulfilled') {
                        convList.push(ConversationStore.fromStore(this.ctx, item.value));
                    }
                    else {
                        const { id } = (_a = item.reason) === null || _a === void 0 ? void 0 : _a.sender;
                        Logger.ctxWarn(this.ctx, `decrypt conversation ${id} error, ${(_b = item.reason) === null || _b === void 0 ? void 0 : _b.message}`);
                        yield this.db.delete('conversation', id);
                    }
                }
                return convList;
            }
            else {
                return list.map(s => ConversationStore.fromStore(this.ctx, s));
            }
        });
    }
    loadMessage() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const list = yield this.db.getAll('message');
            if (this.encryptionEnabled) {
                const dListPromise = list.map(item => decrypt(this.ctx, this._key, item));
                const deMessageRes = yield Promise.allSettled(dListPromise);
                const messageList = [];
                for (const item of deMessageRes) {
                    if (item.status === 'fulfilled') {
                        messageList.push(MessageStore.fromStore(this.ctx, item.value));
                    }
                    else {
                        const { clientId, conversationId } = (_a = item.reason) === null || _a === void 0 ? void 0 : _a.sender;
                        Logger.ctxWarn(this.ctx, `decrypt message ${clientId} error, ${(_b = item.reason) === null || _b === void 0 ? void 0 : _b.message}`);
                        yield this.db.delete('message', MessageStore.formatMessageKey({ clientId, conversationId }));
                    }
                }
                return messageList;
            }
            else {
                return list.map(s => MessageStore.fromStore(this.ctx, s));
            }
        });
    }
    loadParticipants() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const list = yield this.db.getAll('participant');
            if (this.encryptionEnabled) {
                const dListPromise = list.map(item => decrypt(this.ctx, this._key, item));
                const deParticipantRes = yield Promise.allSettled(dListPromise);
                const participantList = [];
                for (const item of deParticipantRes) {
                    if (item.status === 'fulfilled') {
                        participantList.push(ParticipantStore.fromStore(this.ctx, item.value));
                    }
                    else {
                        const { conversationId, userId } = (_a = item.reason) === null || _a === void 0 ? void 0 : _a.sender;
                        Logger.ctxWarn(this.ctx, `decrypt participant ${userId} error, ${(_b = item.reason) === null || _b === void 0 ? void 0 : _b.message}`);
                        yield this.db.delete('participant', ParticipantStore.formatParticipantKey({ conversationId, userId }));
                    }
                }
                return participantList;
            }
            else {
                return list.map(s => ParticipantStore.fromStore(this.ctx, s));
            }
        });
    }
    upsertMessage(m) {
        return __awaiter(this, void 0, void 0, function* () {
            const message = MessageStore.toStore(m);
            if (this.encryptionEnabled) {
                const eMessage = yield encrypt(this.ctx, this._key, message);
                const data = Object.assign({ conversationId: message.conversationId, clientId: message.clientId }, eMessage);
                yield this.db.put('message', data, MessageStore.formatMessageKey(m));
            }
            else {
                yield this.db.put('message', message, MessageStore.formatMessageKey(m));
            }
        });
    }
    upsertConversation(c) {
        return __awaiter(this, void 0, void 0, function* () {
            const conv = ConversationStore.toStore(c);
            if (this.encryptionEnabled) {
                const eConv = yield encrypt(this.ctx, this._key, ConversationStore.toStore(c));
                const data = Object.assign({ id: conv.id }, eConv);
                yield this.db.put('conversation', data);
            }
            else {
                yield this.db.put('conversation', conv);
            }
        });
    }
    upsertParticipant(p) {
        return __awaiter(this, void 0, void 0, function* () {
            const participant = ParticipantStore.toStore(p);
            if (this.encryptionEnabled) {
                const eParticipant = yield encrypt(this.ctx, this._key, participant);
                const data = Object.assign({ conversationId: participant.conversationId, userId: participant.userId }, eParticipant);
                yield this.db.put('participant', data, ParticipantStore.formatParticipantKey(data));
            }
            else {
                yield this.db.put('participant', participant, ParticipantStore.formatParticipantKey(participant));
            }
        });
    }
    deleteMessage(m) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.delete('message', MessageStore.formatMessageKey(m));
        });
    }
    deleteConversation(c) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.clearConversation(c);
            yield this.db.delete('conversation', c.id);
        });
    }
    deleteParticipant(p) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.delete('participant', ParticipantStore.formatParticipantKey(p));
        });
    }
    clearConversation(c) {
        return __awaiter(this, void 0, void 0, function* () {
            // create two transaction to clear conversation
            const clearMsg = () => __awaiter(this, void 0, void 0, function* () {
                var e_1, _a;
                const txMsg = this.db.transaction('message', 'readwrite');
                const indexMsg = txMsg.store.index('__index_con_id');
                try {
                    for (var _b = __asyncValues(indexMsg.iterate(c.id)), _c; _c = yield _b.next(), !_c.done;) {
                        const cursor = _c.value;
                        if (cursor.value.conversationId === c.id) {
                            yield cursor.delete();
                        }
                    }
                }
                catch (e_1_1) { e_1 = { error: e_1_1 }; }
                finally {
                    try {
                        if (_c && !_c.done && (_a = _b.return)) yield _a.call(_b);
                    }
                    finally { if (e_1) throw e_1.error; }
                }
                yield txMsg.done;
            });
            const clearPar = () => __awaiter(this, void 0, void 0, function* () {
                var e_2, _d;
                const txPar = this.db.transaction('participant', 'readwrite');
                const indexPar = txPar.store.index('__index_con_id');
                try {
                    for (var _e = __asyncValues(indexPar.iterate(c.id)), _f; _f = yield _e.next(), !_f.done;) {
                        const cursor = _f.value;
                        if (cursor.value.conversationId === c.id) {
                            yield cursor.delete();
                        }
                    }
                }
                catch (e_2_1) { e_2 = { error: e_2_1 }; }
                finally {
                    try {
                        if (_f && !_f.done && (_d = _e.return)) yield _d.call(_e);
                    }
                    finally { if (e_2) throw e_2.error; }
                }
                yield txPar.done;
            });
            yield Promise.all([
                clearMsg(),
                clearPar()
            ]);
        });
    }
    clear(keepDatabase) {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all([
                this.db.clear('config'),
                this.db.clear('conversation'),
                this.db.clear('cursor'),
                this.db.clear('message'),
                this.db.clear('participant')
            ]);
            if (!keepDatabase) {
                yield deleteDB(IndexedDbDriver.DbNamePrefix + this.currentUserId);
            }
        });
    }
    loadUserCursor(inboxType) {
        return __awaiter(this, void 0, void 0, function* () {
            const value = yield this.db.get('cursor', inboxType);
            return value !== undefined ? Long.fromValue(value) : undefined;
        });
    }
    saveUserCursor(inboxType, cursor) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.put('cursor', cursor.toString(), inboxType);
        });
    }
    saveConfig(key, value) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.put('config', value, key);
        });
    }
    loadConfig(key) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            return (_a = (yield this.db.get('config', key))) !== null && _a !== void 0 ? _a : '';
        });
    }
    deleteConfig(key) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.delete('config', key);
        });
    }
}
IndexedDbDriver.DbNamePrefix = 'imsdk';
IndexedDbDriver.DbVersion = 1;

export { IndexedDbDriver };
//# sourceMappingURL=idb.js.map
